import React from "react";
import "./Tag.scss";
import ClassNames from "classnames";
import {
  TagEntities,
  TagIcons,
  type TagEntity,
  type TagType,
} from "./TagUtils";
import { Tooltip } from "../../../components";
import { useIcon } from "../../../assets/useIcon";

export interface TagShape {
  name?: string;
  type?: string;
  icon?: string;
  svgIcon?: string;
  disabled?: boolean;
  readOnly?: boolean;
  clickable?: boolean;
  tooltip?: string;
  [key: string]: unknown;
}

export interface TagTooltipData {
  dataTip?: string;
  className?: string;
}

export interface TagProps {
  tag?: TagShape;
  type?: TagType;
  onDelete?: React.MouseEventHandler<HTMLElement>;
  wrapperClassName?: string;
  onClick?: React.MouseEventHandler<HTMLDivElement> | null;
  entity?: TagEntity;
  chipStyle?: boolean;
  tooltipData?: TagTooltipData;
  icon?: string;
  svgIcon?: string;
}

const Tag: React.FC<TagProps> = ({
  tag,
  type,
  onDelete,
  wrapperClassName,
  onClick = null,
  entity = TagEntities.RESOURCE,
  chipStyle = false,
  tooltipData = {},
  icon,
  svgIcon,
}) => {
  const { getSvg } = useIcon();
  const finalIcon =
    icon ||
    tag?.icon ||
    (type ? TagIcons[type as keyof typeof TagIcons] : undefined);
  const finalSvgIcon = svgIcon || tag?.svgIcon;
  const hasDelete = !tag?.disabled && !tag?.readOnly;
  const classNameWrapper = ClassNames(
    "tag-wrapper",
    wrapperClassName,
    tag?.type || "",
    {
      "has-tip": !!tooltipData?.dataTip,
      "is-disabled": tag?.disabled,
      "is-readonly": tag?.readOnly,
      "has-delete": hasDelete,
      [`is-${entity}`]: entity,
      "is-chip": !!chipStyle,
      "is-tag": !chipStyle,
      "has-svg-icon": !!finalSvgIcon,
      "has-icon": !!finalIcon,
      clickable: tag?.clickable,
    }
  );

  return (
    <Tooltip
      type="info"
      content={tooltipData?.dataTip}
      className={tooltipData?.className}
    >
      <div
        className={classNameWrapper}
        onClick={(e) => !tag?.disabled && onClick?.(e)}
        data-testid="tag-wrapper"
      >
        {!!finalIcon ? (
          <i className={`seem-icon ${finalIcon} tag-icon`} />
        ) : (
          finalSvgIcon && <img src={getSvg(finalSvgIcon)} alt={finalSvgIcon} />
        )}

        <Tooltip content={tag?.tooltip || tag?.name}>
          <span className="tag-name">{tag?.name}</span>
        </Tooltip>

        {hasDelete && (
          <Tooltip type="info" content="Remove">
            <i
              className="seem-icon seem-icon-close remove-tag-btn"
              onClick={onDelete}
            />
          </Tooltip>
        )}
      </div>
    </Tooltip>
  );
};

export default Tag;
